import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Alert, AlertIcon, Box, Button, FormControl, FormErrorMessage, FormHelperText,
  FormLabel, Input, Select, Text, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay,
} from '@chakra-ui/react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { UNIT_OPTIONS, normalizeUnit, suggestFactor, makeConversion, parseConversion } from './itemUnitConversion';
import './itemRegister.css';

const API = 'https://api.rcb.tdrsoftware.in/api';
const emptyDraft = () => ({ name: '', companyName: '', groupName: '', purchaseUnit: '',
  usageUnit: '', factor: '', store: '', cgst: '0', sgst: '0', hsnNo: '', openingStock: '0', closingStock: '0' });
const messageFrom = error => error.response?.data?.message || error.response?.data?.error || error.message || 'Unable to save. Please try again.';
const asText = value => value == null ? '' : String(value);

function Field({ name, label, error, hint, children, required = false }) {
  const id = useId();
  return <FormControl id={`item-${name}-${id}`} isRequired={required} isInvalid={!!error} minW={0}>
    <FormLabel>{label}</FormLabel>
    {React.cloneElement(children, { 'aria-label': label })}
    {error ? <FormErrorMessage role="alert">{error}</FormErrorMessage> : hint && <FormHelperText>{hint}</FormHelperText>}
  </FormControl>;
}

function Section({ title, description, children }) {
  return <section className="item-register-section">
    <h2>{title}</h2>
    {description && <p className="item-section-description">{description}</p>}
    <div className="item-register-grid">{children}</div>
  </section>;
}

export default function ItemRegister() {
  const customerId = localStorage.getItem('customerId');
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [editing, setEditing] = useState(null);
  const [errors, setErrors] = useState({});
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const cancelRef = useRef();
  const nameRef = useRef();
  const submitting = useRef(false);
  const loadSequence = useRef(0);

  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoading(true); setLoadError('');
    try {
      const [itemResult, storeResult] = await Promise.all([
        axios.get(`${API}/item/${customerId}`), axios.get(`${API}/store/${customerId}`),
      ]);
      if (sequence !== loadSequence.current) return;
      if (!Array.isArray(itemResult.data.item) || !Array.isArray(storeResult.data)) throw new Error('Unable to read items or stores. Please retry.');
      setItems(itemResult.data.item);
      setStores([...storeResult.data].sort((a, b) => Number(a.index ?? 0) - Number(b.index ?? 0) ||
        String(a.name).localeCompare(String(b.name), undefined, { numeric: true })));
    } catch (error) {
      if (sequence === loadSequence.current) { setLoadError(messageFrom(error)); setItems([]); setStores([]); }
    } finally { if (sequence === loadSequence.current) setLoading(false); }
  }, [customerId]);

  useEffect(() => { load(); return () => { loadSequence.current += 1; }; }, [load]);

  const change = event => {
    const { name, value } = event.target;
    setDraft(previous => {
      const next = { ...previous, [name]: value };
      if (name === 'purchaseUnit' || name === 'usageUnit') next.factor = suggestFactor(next.purchaseUnit, next.usageUnit);
      if (name === 'openingStock' && !editing) next.closingStock = value;
      return next;
    });
    setErrors(previous => ({ ...previous, [name]: undefined, ...(name.includes('Unit') ? { factor: undefined } : {}) }));
    setSaveError('');
  };

  const cancelEdit = () => { setEditing(null); setDraft(emptyDraft()); setErrors({}); setSaveError(''); };
  const edit = item => {
    let factor = '';
    try { factor = String(parseConversion(item).factor); } catch (_) { /* Preserve legacy definitions; never guess. */ }
    setEditing(item);
    setDraft({ ...emptyDraft(), ...Object.fromEntries(Object.keys(emptyDraft()).map(key => [key, asText(item[key])])),
      purchaseUnit: normalizeUnit(item.purchaseUnit), usageUnit: normalizeUnit(item.usageUnit), factor });
    setErrors({}); setSaveError(''); nameRef.current?.focus();
  };

  let conversion = null;
  let conversionError = '';
  if (!editing && draft.purchaseUnit && draft.usageUnit) {
    try { conversion = makeConversion(draft); } catch (error) { conversionError = error.message; }
  }
  const standardFactor = suggestFactor(draft.purchaseUnit, draft.usageUnit);
  const validate = () => {
    const next = {};
    ['name', 'companyName', 'groupName', 'store', 'hsnNo'].forEach(key => {
      if (!draft[key].trim()) next[key] = 'This field is required.';
    });
    if (draft.hsnNo && !/^\d+$/.test(draft.hsnNo.trim())) next.hsnNo = 'Enter a numeric HSN identifier.';
    ['cgst', 'sgst'].forEach(key => {
      if (draft[key].trim() === '' || !Number.isFinite(Number(draft[key])) || Number(draft[key]) < 0 || Number(draft[key]) > 100)
        next[key] = 'Enter a percentage from 0 to 100.';
    });
    if (!editing) {
      if (!draft.purchaseUnit) next.purchaseUnit = 'Choose a purchase unit.';
      if (!draft.usageUnit) next.usageUnit = 'Choose a usage unit.';
      if (!conversion) next.factor = conversionError || 'Enter a positive conversion factor.';
      if (!/^\d+(?:\.\d{1,6})?$/.test(draft.openingStock) || !Number.isFinite(Number(draft.openingStock)))
        next.openingStock = 'Enter a nonnegative quantity with up to six decimals.';
      if (!stores.some(store => store.name === draft.store)) next.store = 'Choose an available store.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async event => {
    event.preventDefault();
    if (submitting.current || !validate()) return;
    submitting.current = true; setBusy(true); setSaveError('');
    const data = { name: draft.name.trim(), companyName: draft.companyName.trim(), groupName: draft.groupName.trim(),
      hsnNo: draft.hsnNo.trim(), cgst: Number(draft.cgst), sgst: Number(draft.sgst), store: draft.store };
    if (editing) Object.assign(data, { purchaseUnit: editing.purchaseUnit, usageUnit: editing.usageUnit, equivalentNo: editing.equivalentNo });
    else Object.assign(data, { purchaseUnit: conversion.purchaseUnit, usageUnit: conversion.usageUnit,
      equivalentNo: conversion.equivalentNo, openingStock: Number(draft.openingStock), closingStock: Number(draft.openingStock) });
    try {
      const response = editing ? await axios.put(`${API}/itemUpdate/${customerId}/${editing._id}`, { data }) :
        await axios.post(`${API}/itemRegister`, { customerId, data });
      toast.success(response.data.message || 'Item saved.'); cancelEdit(); await load();
    } catch (error) { setSaveError(messageFrom(error)); }
    finally { submitting.current = false; setBusy(false); }
  };

  const remove = async () => {
    if (submitting.current || !deleting) return;
    submitting.current = true; setBusy(true);
    try { await axios.delete(`${API}/itemDelete/${customerId}/${deleting._id}`); setDeleting(null); await load(); }
    catch (error) { toast.error(messageFrom(error)); }
    finally { submitting.current = false; setBusy(false); }
  };

  const input = (name, props = {}) => <Input name={name} value={draft[name]} onChange={change} minH="44px" bg="white" {...props} />;
  const unitSelect = name => <Select name={name} value={draft[name]} onChange={change} isDisabled={!!editing || busy} minH="44px" bg="white">
    <option value="">Choose unit</option>
    {draft[name] && !UNIT_OPTIONS.some(unit => unit.value === draft[name]) && <option value={draft[name]}>{draft[name]} (saved unit)</option>}
    {UNIT_OPTIONS.map(unit => <option key={unit.value} value={unit.value}>{unit.label}</option>)}
  </Select>;

  return <Box className="item-register-page" color="gray.800">
    <header className="item-register-heading"><div><h1>Item Register</h1><p>Item details, unit conversion and initial stock.</p></div>
      <Text className="item-register-mode">{editing ? 'Editing item' : 'New item'}</Text>
    </header>
    {loadError && <Alert status="error" mb={4}><AlertIcon /><Box flex="1">{loadError}</Box><Button onClick={load}>Retry</Button></Alert>}
    <Box as="form" onSubmit={save} noValidate aria-label="Item registration">
      <fieldset disabled={busy} className="item-register-fieldset">
        <Section title="Item details">
          <Field name="name" label="Item name" required error={errors.name}>{input('name', { ref: nameRef, autoComplete: 'off' })}</Field>
          <Field name="companyName" label="Company name" required error={errors.companyName}>{input('companyName')}</Field>
          <Field name="groupName" label="Group name" required error={errors.groupName}>{input('groupName')}</Field>
        </Section>
        <Section title="Units & conversion" description="Define how one purchase unit converts to the unit used for stock transfers.">
          <Field name="purchaseUnit" label="Purchase unit" required error={errors.purchaseUnit}>{unitSelect('purchaseUnit')}</Field>
          <Field name="usageUnit" label="Usage unit" required error={errors.usageUnit}>{unitSelect('usageUnit')}</Field>
          <Field name="factor" label="Conversion factor" required error={errors.factor}
            hint={editing ? 'Saved factor is protected.' : standardFactor ? 'Standard conversion — filled automatically.' : 'Usage units in one purchase unit; enter your box/pack size.'}>
            {input('factor', { type: 'number', min: '0.000001', step: 'any', inputMode: 'decimal', isReadOnly: !!editing || !!standardFactor })}
          </Field>
        </Section>
        <div className="item-conversion-preview" aria-live="polite">
          <span>Conversion preview</span>
          <strong>{editing ? editing.equivalentNo || 'Legacy conversion unavailable' : conversion?.equivalentNo || 'Choose units and enter a valid factor'}</strong>
        </div>
        {editing && <Text className="item-protection-note">Saved units, conversion, store and stock are protected to avoid changing existing balances. A conversion change needs a stock review.</Text>}
        <Section title="Stock" description={editing ? 'Saved stock values are shown for reference; they are not recalculated here.' : 'Opening stock is entered in the purchase unit. Initial closing stock equals opening stock.'}>
          <Field name="store" label="Store" required error={errors.store}>
            <Select name="store" value={draft.store} onChange={change} isDisabled={!!editing || loading || busy} minH="44px" bg="white">
              <option value="">Choose store</option>
              {editing && !stores.some(store => store.name === draft.store) && <option value={draft.store}>{draft.store} (saved store)</option>}
              {stores.map(store => <option key={store._id || store.name} value={store.name}>{store.name}</option>)}
            </Select>
          </Field>
          <Field name="openingStock" label="Opening stock" error={errors.openingStock} hint={`Unit: ${draft.purchaseUnit || 'choose a purchase unit'}`}>
            {input('openingStock', { type: 'number', step: 'any', min: '0', inputMode: 'decimal', isReadOnly: !!editing })}
          </Field>
          <Field name="closingStock" label="Closing stock" hint="Read-only stock value.">
            {input('closingStock', { type: 'number', step: 'any', isReadOnly: true })}
          </Field>
        </Section>
        <Section title="Tax & HSN">
          <Field name="cgst" label="CGST (%)" required error={errors.cgst}>{input('cgst', { type: 'number', min: '0', max: '100', step: 'any' })}</Field>
          <Field name="sgst" label="SGST (%)" required error={errors.sgst}>{input('sgst', { type: 'number', min: '0', max: '100', step: 'any' })}</Field>
          <Field name="hsnNo" label="HSN number" required error={errors.hsnNo}>{input('hsnNo', { inputMode: 'numeric' })}</Field>
        </Section>
      </fieldset>
      {saveError && <Alert status="error" mt={4}><AlertIcon />{saveError}</Alert>}
      <div className="item-register-actions">
        <Button type="submit" bg="#FEA116" color="#0F172B" minH="44px" isLoading={busy} isDisabled={loading || !!loadError || busy}>
          {editing ? 'Update item' : 'Save item'}
        </Button>
        {editing && <Button type="button" variant="outline" minH="44px" onClick={cancelEdit} isDisabled={busy}>Cancel edit</Button>}
      </div>
    </Box>
    <section className="item-register-section item-register-list">
      <h2>Registered items</h2>
      <p className="item-section-description">{loading ? 'Loading items…' : `${items.length} items`} · Scroll the table to view all details.</p>
      <TableContainer role="region" aria-label="Registered items table" tabIndex={0} width="100%" overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead><Tr>{['Item', 'Company / group', 'Conversion', 'Store', 'Opening', 'Closing', 'CGST / SGST', 'HSN', 'Actions'].map(title => <Th key={title}>{title}</Th>)}</Tr></Thead>
          <Tbody>{items.map(item => <Tr key={item._id}>
            <Td fontWeight="600">{item.name}</Td><Td>{item.companyName}<br /><Text as="span" color="gray.600">{item.groupName}</Text></Td>
            <Td>{item.equivalentNo || 'Not defined'}</Td><Td>{item.store}</Td>
            <Td isNumeric>{item.openingStock ?? '—'} {item.purchaseUnit}</Td><Td isNumeric>{item.closingStock ?? '—'} {item.purchaseUnit}</Td>
            <Td>{item.cgst ?? '—'}% / {item.sgst ?? '—'}%</Td><Td>{item.hsnNo ?? '—'}</Td>
            <Td><Box display="flex" gap={2}><Button minH="44px" size="sm" onClick={() => edit(item)} isDisabled={busy} aria-label={`Edit ${item.name}`}>Edit</Button>
              <Button minH="44px" size="sm" variant="outline" colorScheme="red" onClick={() => setDeleting(item)} isDisabled={busy} aria-label={`Delete ${item.name}`}>Delete</Button></Box></Td>
          </Tr>)}</Tbody>
        </Table>
      </TableContainer>
      {!loading && !loadError && items.length === 0 && <Text py={6}>No items registered yet.</Text>}
    </section>
    <AlertDialog isOpen={!!deleting} leastDestructiveRef={cancelRef} onClose={() => !busy && setDeleting(null)}>
      <AlertDialogOverlay><AlertDialogContent mx={4}><AlertDialogHeader>Delete item?</AlertDialogHeader>
        <AlertDialogBody>Delete {deleting?.name}? This permanently removes the item. Do not delete an item with stock or business history.</AlertDialogBody>
        <AlertDialogFooter><Button ref={cancelRef} onClick={() => setDeleting(null)} isDisabled={busy}>Cancel</Button>
          <Button colorScheme="red" ml={3} onClick={remove} isLoading={busy}>Delete</Button></AlertDialogFooter>
      </AlertDialogContent></AlertDialogOverlay>
    </AlertDialog>
  </Box>;
}
